import { InfoMenu } from "@src/components/elements/InfoMenu";
import { WithHyperlink } from "@src/components/elements/WithHyperlink";
import React, { CSSProperties, ChangeEventHandler, ReactNode } from "react";
import styled from "styled-components/macro";

type StyledTextInputRootProps = {
  error?: string;
  size?: "small" | "large";
  bare?: boolean;
  hasPrefix?: boolean;
  labelType: "normal" | "floating";
  disabled?: boolean;
  fullWidth?: boolean;
};

export const StyledTextInputRoot = styled.div<StyledTextInputRootProps>`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  position: relative;
  margin-top: ${(props) =>
    props.labelType === "floating" ? "0.5rem" : undefined};

  label {
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
    ${(props) => (props.bare ? "display: none;" : null)}
    display: flex;
    align-items: center;
    gap: 0.5rem;
    user-select: none;
    ${(props) =>
      props.labelType === "floating" ? "pointer-events: none;" : ""}
  }

  .error,
  .required {
    color: var(--error-color);
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
  }

  .error {
    ${(props) => (props.bare ? "display: none;" : null)}
  }

  .input-container {
    position: relative;

    .floating-label-input {
      &::placeholder {
        color: var(--border-gray);
      }
      &:focus::placeholder {
        color: transparent;
      }
    }

    .floating-label {
      position: absolute;
      transition: all 0.3s ease;
      user-select: none;
      pointer-events: none;
    }

    input:not(:focus) + .floating-label {
      top: 50%;
      // same styling as the padding but only the side padding is taken
      left: ${(props) =>
        props.size === "small"
          ? props.hasPrefix
            ? "1.25rem"
            : "0.5rem"
          : props.hasPrefix
          ? "2.5rem"
          : "0.75rem"};
      transform: translateY(-50%);
      color: transparent;
    }

    input:focus + .floating-label,
    input:not(:placeholder-shown) + .floating-label {
      background-color: var(--page-background);
      top: 0px;
      left: 0.25rem;
      transform: translateY(-50%);
      padding: 0 0.25rem;
      color: var(--secondary-black);

      #required {
        color: transparent;
      }
    }
  }

  input {
    ${({ fullWidth }) => (fullWidth ? "width: 100%;" : null)}
    max-width: 100%;
    border: 1px solid
      ${(props) => (props.error ? "var(--error-color)" : "var(--border-gray)")};
    border-radius: 0.25em;
    padding: ${(props) =>
      props.size === "small"
        ? props.hasPrefix
          ? "0.375rem 0.5rem 0.375rem 1.25rem"
          : "0.375rem 0.5rem"
        : props.hasPrefix
        ? "0.75rem 2.5rem"
        : "0.75rem"};
    font-family: var(--inter-font);
    font-weight: var(--font-weight-regular);
    font-size: ${(props) =>
      props.size === "small"
        ? "var(--font-size-small)"
        : "var(--font-size-small)"};
    transition: all 0.3s ease;
    flex-grow: 1;
    background-color: ${(props) =>
      props.disabled ? "var(--background-gray)" : "var(--white)"};

    &:hover {
      border-color: var(--dark-gray);
      box-shadow: var(--drop-shadow-light);
    }

    &:focus {
      outline: none;
      border-color: var(--green);
      box-shadow: var(--drop-shadow-light);
    }

    &::placeholder {
      font-size: var(--font-size-small);
      color: var(--border-gray);
    }
  }

  .prefix {
    position: absolute;
    top: ${(props) => (props.size === "small" ? "0.35rem" : "0.5rem")};
    left: 0.5rem;
  }
`;

export type InputLabelProps =
  | {
      labelType?: "normal";
      label?: string;
    }
  | {
      labelType: "floating";
      label: string;
    };

export type TextInputProps = {
  name: string;
  initialValue?: string;
  value?: string | number | null;
  type?:
    | "text"
    | "email"
    | "search"
    | "url"
    | "number"
    | "password"
    | undefined;
  step?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  className?: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  hideError?: boolean;
  size?: "large" | "small";
  postfix?: string | ReactNode;
  bare?: boolean;
  prefix?: string | ReactNode;
  tabIndex?: number;
  disabled?: boolean;
  tooltipText?: string;
  fullWidth?: boolean;
  style?: CSSProperties;
  inputClassName?: string;
} & InputLabelProps;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      label,
      tooltipText,
      value,
      initialValue,
      type,
      step,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      isRequired,
      className,
      error,
      hideError,
      size,
      postfix,
      bare,
      prefix,
      tabIndex,
      onClick,
      labelType = "normal",
      disabled = false,
      fullWidth = false,
      style,
      inputClassName,
    },
    ref,
  ) => {
    return (
      <StyledTextInputRoot
        style={style}
        labelType={labelType}
        className={`${error ? "field-error" : ""} ${className ?? ""}`}
        error={error}
        size={size || "large"}
        bare={bare}
        hasPrefix={!!prefix}
        disabled={disabled}
        onClick={onClick}
        fullWidth={fullWidth}
      >
        {label && labelType === "normal" && (
          <label htmlFor={label}>
            <WithHyperlink as="span" str={label} className="label" />
            {isRequired && <span className="required">*</span>}
            {tooltipText && <InfoMenu title={<span>{tooltipText}</span>} />}
          </label>
        )}
        {prefix && <span className="prefix">{prefix}</span>}
        <div className="row input-container">
          <input
            name={name}
            ref={ref}
            id={label}
            type={type || "text"}
            step={type === "number" ? step || "1" : undefined}
            value={value !== null ? value : ""}
            defaultValue={initialValue}
            placeholder={labelType === "floating" ? label : placeholder}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            tabIndex={tabIndex}
            className={
              labelType === "floating"
                ? `floating-label-input ${inputClassName}`
                : inputClassName
            }
            disabled={disabled}
          />
          {labelType === "floating" ? (
            <label htmlFor={name} className="floating-label label">
              {label}
              {isRequired && <span className="required">*</span>}
            </label>
          ) : undefined}
          {postfix && <span>{postfix}</span>}
        </div>
        {!hideError && <div className="error">{error}&nbsp;</div>}
      </StyledTextInputRoot>
    );
  },
);

TextInput.displayName = "TextInput";
